import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { AppLayout } from "./DashboardPage";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  Building2,
  Upload,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Loader2,
  FileUp,
  ArrowRight,
  Clock,
  Unlink,
  Download,
  ChevronDown,
  ChevronUp,
  Calendar,
  Wallet,
  Shield,
  ExternalLink
} from "lucide-react";
import ConnectionsPanel from "../components/ConnectionsPanel";
import BankConnectionConsentPanel from "../components/BankConnectionConsentPanel";

// Relative time formatter
function formatSyncTime(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  if (diffHr < 24) {
    const isToday = date.toDateString() === now.toDateString();
    const time = date.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit' });
    return isToday ? `Today ${time}` : `Yesterday ${time}`;
  }
  return date.toLocaleDateString('en-NZ', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ===== CALM UX: Import Success Component =====
function ImportSuccessCard({ result, onStartReconcile }) {
  const { imported_count, duplicate_count, error_count } = result;
  
  return (
    <div className="mt-6 p-6 rounded-xl bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200/50 dark:border-emerald-800/30" data-testid="import-success-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center flex-shrink-0">
          <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div className="flex-1">
          <h3 className="font-medium text-emerald-900 dark:text-emerald-100 mb-1">
            Transactions imported
          </h3>
          <p className="text-sm text-emerald-700 dark:text-emerald-300 mb-4">
            {imported_count} transaction{imported_count !== 1 ? 's' : ''} added to your account.
            {duplicate_count > 0 && ` ${duplicate_count} duplicate${duplicate_count !== 1 ? 's' : ''} skipped.`}
            {error_count > 0 && ` ${error_count} row${error_count !== 1 ? 's' : ''} couldn't be read.`}
          </p>
          
          <Button 
            onClick={onStartReconcile}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="start-weekly-close-btn"
          >
            Start your reset
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ===== CALM UX: Import Error Component =====
function ImportErrorCard({ error, onRetry }) {
  return (
    <div className="mt-6 p-6 rounded-xl bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-800/30">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center flex-shrink-0">
          <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1">
          <h3 className="font-medium text-amber-900 dark:text-amber-100 mb-1">
            Import didn't complete
          </h3>
          <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
            {error || "Something went wrong. This sometimes happens with unusual file formats."}
          </p>
          <Button 
            variant="outline"
            onClick={onRetry}
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            Try again
          </Button>
        </div>
      </div>
    </div>
  );
}

// ===== CALM UX: Step Indicator =====
function StepIndicator({ step, isActive, isComplete }) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
      isActive 
        ? 'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800' 
        : isComplete
          ? 'bg-muted/30'
          : 'opacity-50'
    }`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
        isComplete 
          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300'
          : isActive
            ? 'bg-emerald-600 text-white'
            : 'bg-muted text-muted-foreground'
      }`}>
        {isComplete ? <CheckCircle2 className="w-4 h-4" /> : step.number}
      </div>
      <div>
        <p className={`text-sm font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
          {step.title}
        </p>
        <p className="text-xs text-muted-foreground">{step.description}</p>
      </div>
    </div>
  );
}

// ===== Import History Item (Compact) =====
function ImportHistoryItem({ batch, accounts, householdId, onUndo }) {
  const [undoing, setUndoing] = useState(false);
  const account = accounts.find(a => a.account_id === batch.account_id);
  const date = new Date(batch.created_at);
  const isUndone = batch.status === 'undone';
  
  const handleUndo = async () => {
    if (!window.confirm('This will remove all transactions from this import. Continue?')) return;
    
    setUndoing(true);
    try {
      const res = await api.post(
        `/api/households/${householdId}/transactions/import-batches/${batch.batch_id}/undo`,
        {}
      );
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        onUndo();
      } else {
        toast.error('Could not undo import');
      }
    } catch (error) {
      toast.error('Could not undo import');
    } finally {
      setUndoing(false);
    }
  };
  
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${isUndone ? 'opacity-50 bg-muted/30' : 'bg-card'}`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="text-xs text-muted-foreground">
          {date.toLocaleDateString('en-NZ', { month: 'short', day: 'numeric' })}
        </div>
        <div className="truncate">
          <span className="text-sm font-medium">{batch.imported_count} transactions</span>
          <span className="text-xs text-muted-foreground ml-2">
            {account?.display_name || account?.name || 'Account'}
          </span>
        </div>
        {isUndone && <Badge variant="secondary" className="text-xs">Undone</Badge>}
      </div>
      
      {!isUndone && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleUndo}
          disabled={undoing}
          className="text-muted-foreground hover:text-destructive"
        >
          {undoing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
        </Button>
      )}
    </div>
  );
}

// ===== Akahu Dev Mode Card =====
function AkahuDevModeCard({ status, connection, onSync, syncing, householdId }) {
  const isConfigured = status?.dev_mode?.configured;
  const isConnected = connection?.connected;
  const lastSynced = formatSyncTime(connection?.last_synced_at);
  
  if (!status?.dev_mode?.enabled) return null;
  
  // Don't show configuration hints in production - only show when dev mode is enabled
  // but tokens not configured (for local development only)
  const showConfigHint = !isConfigured && process.env.NODE_ENV !== 'production';
  
  return (
    <div className="p-4 rounded-xl bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-800/30" data-testid="akahu-dev-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-amber-900 dark:text-amber-100">Akahu</h4>
              <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs" data-testid="dev-mode-badge">
                Dev Mode
              </Badge>
            </div>
            <p className="text-xs text-amber-700 dark:text-amber-300">
              {isConnected 
                ? `${connection?.accounts_linked || 0} account(s) linked`
                : isConfigured 
                  ? "Ready to sync" 
                  : "Not connected"
              }
            </p>
          </div>
        </div>
        {isConnected && (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
            Connected
          </Badge>
        )}
      </div>
      
      {/* Last Synced timestamp */}
      {lastSynced && (
        <div className="flex items-center justify-between pt-3 border-t border-amber-200/50 dark:border-amber-800/30">
          <div className="text-xs text-amber-600 dark:text-amber-400">
            <Clock className="w-3 h-3 inline mr-1" />
            Last synced: {lastSynced}
            {connection?.sync_stats && (
              <span className="ml-2">
                ({connection.sync_stats.imported} imported)
              </span>
            )}
          </div>
          <Button
            onClick={onSync}
            disabled={syncing || !isConfigured}
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="sync-akahu-dev-btn"
          >
            {syncing ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-1" />
                Sync Now
              </>
            )}
          </Button>
        </div>
      )}
      
      {/* Initial sync button when configured but not yet synced */}
      {isConfigured && !isConnected && (
        <div className="pt-3 border-t border-amber-200/50 dark:border-amber-800/30">
          <Button
            onClick={onSync}
            disabled={syncing}
            size="sm"
            className="w-full bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="connect-sync-akahu-dev-btn"
          >
            {syncing ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-1" />
                Connect & Sync
              </>
            )}
          </Button>
        </div>
      )}
      
      {/* Config hint only in development */}
      {showConfigHint && (
        <div className="mt-3 p-2 rounded bg-amber-100/50 dark:bg-amber-900/30 text-xs text-amber-700 dark:text-amber-300">
          Add AKAHU_APP_TOKEN and AKAHU_USER_TOKEN to backend/.env
        </div>
      )}
    </div>
  );
}

// ===== Akahu Connection Card =====
function AkahuConnectionCard({ connection, onSync, onDisconnect, syncing }) {
  if (!connection?.connected) return null;
  
  const lastSynced = formatSyncTime(connection.last_synced_at) || 'Never';
  
  return (
    <div className="p-4 rounded-xl bg-teal-50/50 dark:bg-teal-950/20 border border-teal-200/50 dark:border-teal-800/30">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <h4 className="font-medium text-teal-900 dark:text-teal-100">Bank connected</h4>
            <p className="text-xs text-teal-700 dark:text-teal-300">
              We'll keep transactions up to date.
            </p>
          </div>
        </div>
        <Badge className="bg-teal-100 text-teal-700 border-teal-200">
          Active
        </Badge>
      </div>
      
      <div className="flex items-center justify-between pt-3 border-t border-teal-200/50 dark:border-teal-800/30">
        <div className="text-xs text-teal-600 dark:text-teal-400">
          <Clock className="w-3 h-3 inline mr-1" />
          Last synced: {lastSynced}
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onSync}
            disabled={syncing}
            className="text-teal-700 hover:text-teal-800 hover:bg-teal-100"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-1">Refresh</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDisconnect}
            className="text-muted-foreground hover:text-destructive"
          >
            Disconnect
          </Button>
        </div>
      </div>
      
      {connection.last_sync_error && (
        <div className="mt-3 p-2 rounded bg-amber-50 dark:bg-amber-900/30 text-xs text-amber-700 dark:text-amber-300">
          Connection paused — reconnect when you're ready.
        </div>
      )}
    </div>
  );
}

// ===== Main Page =====
export default function IntegrationsPage() {
  const { household, accounts } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [importHistory, setImportHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // CSV state
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);
  
  // Akahu state
  const [akahuStatus, setAkahuStatus] = useState(null);
  const [akahuConnection, setAkahuConnection] = useState(null);
  const [akahuSyncing, setAkahuSyncing] = useState(false);
  const [showBankConsentPanel, setShowBankConsentPanel] = useState(false);
  const [connectingBank, setConnectingBank] = useState(false);
  
  // Akahu Dev Mode state
  const [akahuDevConnection, setAkahuDevConnection] = useState(null);
  const [akahuDevSyncing, setAkahuDevSyncing] = useState(false);
  
  // Current step tracking
  const currentStep = !selectedAccountId ? 1 : !selectedFile ? 2 : 3;
  
  const steps = [
    { number: 1, title: "Choose account", description: "Where should we add these transactions?" },
    { number: 2, title: "Select file", description: "Upload a CSV from your bank" },
    { number: 3, title: "Import", description: "Review and confirm" }
  ];
  
  // Handle URL params (success/error from Akahu OAuth)
  useEffect(() => {
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');
    const message = searchParams.get('message');
    
    if (connected === 'akahu') {
      toast.success("Bank connected. We'll keep transactions up to date.");
      // Clear params
      window.history.replaceState({}, '', '/integrations');
    }
    
    if (error) {
      toast.error(message || "Connection didn't complete. Please try again.");
      window.history.replaceState({}, '', '/integrations');
    }
  }, [searchParams]);

  // Fetch data
  useEffect(() => {
    if (household) {
      fetchData();
    }
  }, [household]);

  const fetchData = async () => {
    try {
      const [historyRes, akahuStatusRes, akahuConnRes] = await Promise.all([
        api.get(`/api/households/${household.household_id}/transactions/import-batches`),
        api.akahu.getStatus(),
        api.akahu.getConnection(household.household_id)
      ]);

      if (historyRes.ok) setImportHistory(await historyRes.json());
      if (akahuStatusRes.ok) {
        const statusData = await akahuStatusRes.json();
        setAkahuStatus(statusData);
        
        // If dev mode is enabled, fetch dev connection status
        if (statusData?.dev_mode?.enabled) {
          const devConnRes = await api.akahu.dev.getConnection(household.household_id);
          if (devConnRes.ok) {
            setAkahuDevConnection(await devConnRes.json());
          }
        }
      }
      if (akahuConnRes.ok) setAkahuConnection(await akahuConnRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
      setImportError(null);
    }
  };
  
  // Handle CSV upload
  const handleUpload = async () => {
    if (!selectedFile || !selectedAccountId) return;
    
    setUploading(true);
    setImportResult(null);
    setImportError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('account_id', selectedAccountId);
      
      const res = await api.post(
        `/api/households/${household.household_id}/transactions/import`,
        formData
      );
      
      if (res.ok) {
        const data = await res.json();
        setImportResult(data);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchData(); // Refresh history
      } else {
        const err = await res.json();
        setImportError(err.detail || 'Import failed');
      }
    } catch (error) {
      setImportError('Could not connect to server');
    } finally {
      setUploading(false);
    }
  };
  
  // Download CSV template
  const downloadTemplate = () => {
    const template = `date,description,amount
2025-12-15,Grocery Store,-45.50
2025-12-14,Coffee Shop,-5.75
2025-12-13,Salary Deposit,2500.00`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ara_wealth_template.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };
  
  // Akahu connect - show consent panel first
  const connectAkahu = () => {
    setShowBankConsentPanel(true);
  };
  
  // Handle consent confirmed - redirect to Akahu
  const handleBankConnectionConfirmed = () => {
    setConnectingBank(true);
    window.location.href = api.akahu.getStartUrl(household.household_id);
  };
  
  // Akahu sync
  const syncAkahu = async () => {
    setAkahuSyncing(true);
    try {
      const res = await api.akahu.syncTransactions(household.household_id);
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || `Synced ${data.imported_count} transactions`);
        fetchData();
      } else {
        toast.error('Sync failed');
      }
    } catch (error) {
      toast.error('Could not sync');
    } finally {
      setAkahuSyncing(false);
    }
  };
  
  // Akahu disconnect
  const disconnectAkahu = async () => {
    if (!window.confirm('Disconnect your bank? You can reconnect anytime.')) return;
    
    try {
      const res = await api.akahu.disconnect(household.household_id);
      if (res.ok) {
        toast.success('Bank disconnected');
        setAkahuConnection(null);
        fetchData();
      }
    } catch (error) {
      toast.error('Could not disconnect');
    }
  };
  
  // Akahu Dev Mode sync
  const syncAkahuDev = async () => {
    setAkahuDevSyncing(true);
    try {
      const res = await api.akahu.dev.sync(household.household_id, 90);
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || `Synced ${data.imported_count} transactions`);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Sync failed');
      }
    } catch (error) {
      toast.error('Could not sync');
    } finally {
      setAkahuDevSyncing(false);
    }
  };
  
  // Navigate to reconcile
  const goToReconcile = () => {
    navigate('/reconcile');
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="integrations-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-semibold mb-2">Import Transactions</h1>
          <p className="text-muted-foreground">
            Get your bank transactions into ARA so you can complete your review & reset.
          </p>
        </div>
        
        {/* Akahu Dev Mode Card (if enabled) */}
        {akahuStatus?.dev_mode?.enabled && (
          <div className="mb-6">
            <AkahuDevModeCard
              status={akahuStatus}
              connection={akahuDevConnection}
              onSync={syncAkahuDev}
              syncing={akahuDevSyncing}
              householdId={household.household_id}
            />
          </div>
        )}
        
        {/* Akahu Connection (if enabled and connected via OAuth) */}
        {akahuStatus?.enabled && !akahuStatus?.dev_mode?.enabled && akahuConnection?.connected && (
          <div className="mb-8">
            <AkahuConnectionCard 
              connection={akahuConnection}
              onSync={syncAkahu}
              onDisconnect={disconnectAkahu}
              syncing={akahuSyncing}
            />
          </div>
        )}
        
        {/* Main Import Card */}
        <Card className="mb-6">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">CSV Import</CardTitle>
                <CardDescription>
                  Export transactions from your bank's website and upload them here
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={downloadTemplate}
                className="text-muted-foreground"
                data-testid="download-template-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                Template
              </Button>
            </div>
          </CardHeader>
          
          <CardContent>
            {/* Step indicators */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
              {steps.map((step) => (
                <StepIndicator
                  key={step.number}
                  step={step}
                  isActive={currentStep === step.number}
                  isComplete={currentStep > step.number}
                />
              ))}
            </div>
            
            {/* Step 1: Account selection */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Which account are these transactions from?
                </label>
                <select
                  value={selectedAccountId}
                  onChange={(e) => {
                    setSelectedAccountId(e.target.value);
                    setImportResult(null);
                    setImportError(null);
                  }}
                  className="w-full p-3 border rounded-lg bg-background text-sm"
                  data-testid="account-select"
                >
                  <option value="">Select an account...</option>
                  {accounts.map((acc) => (
                    <option key={acc.account_id} value={acc.account_id}>
                      {(acc.display_name || acc.name)} ({acc.institution || acc.account_type})
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Step 2: File upload */}
              {selectedAccountId && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Upload your CSV file
                  </label>
                  <div 
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer
                      ${selectedFile 
                        ? 'border-emerald-300 bg-emerald-50/50 dark:bg-emerald-950/20' 
                        : 'border-muted-foreground/20 hover:border-muted-foreground/40'
                      }`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      type="file"
                      accept=".csv"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="file-input"
                    />
                    
                    {selectedFile ? (
                      <div>
                        <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-emerald-600" />
                        <p className="font-medium text-emerald-700 dark:text-emerald-300">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Click to change file
                        </p>
                      </div>
                    ) : (
                      <div>
                        <FileUp className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                        <p className="font-medium">Click to select a CSV file</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Most banks let you export transactions as CSV
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Step 3: Import button */}
              {selectedFile && selectedAccountId && (
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12"
                  data-testid="import-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import Transactions
                    </>
                  )}
                </Button>
              )}
            </div>
            
            {/* Success state */}
            {importResult && !importResult.error && (
              <ImportSuccessCard 
                result={importResult} 
                onStartReconcile={goToReconcile}
              />
            )}
            
            {/* Error state */}
            {importError && (
              <ImportErrorCard 
                error={importError}
                onRetry={() => {
                  setImportError(null);
                  setSelectedFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
              />
            )}
          </CardContent>
        </Card>
        
        {/* Akahu Connect Card (if OAuth enabled but not connected, and not in dev mode) */}
        {akahuStatus?.enabled && !akahuStatus?.dev_mode?.enabled && !akahuConnection?.connected && (
          <Card className="mb-6 border-teal-200/50 dark:border-teal-800/30" data-testid="connect-bank-card">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium mb-1">Connect your NZ bank</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Securely link your bank account to automatically import transactions. 
                    Supports ANZ, ASB, BNZ, Kiwibank, Westpac, and more.
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1 mb-4">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-3 h-3 text-teal-500" />
                      Read-only access — we cannot move money
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-3 h-3 text-teal-500" />
                      Powered by Akahu, NZ's open banking provider
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-3 h-3 text-teal-500" />
                      Disconnect anytime from within ARA Wealth
                    </li>
                  </ul>
                  <Button
                    onClick={connectAkahu}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                    data-testid="connect-akahu-btn"
                  >
                    Connect Bank
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Import History (collapsible) */}
        {importHistory.length > 0 && (
          <div className="mt-6">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="toggle-history-btn"
            >
              <Clock className="w-4 h-4" />
              Previous imports ({importHistory.length})
              {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {showHistory && (
              <div className="mt-3 space-y-2">
                {importHistory.slice(0, 10).map((batch) => (
                  <ImportHistoryItem
                    key={batch.batch_id}
                    batch={batch}
                    accounts={accounts}
                    householdId={household.household_id}
                    onUndo={fetchData}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Connected Accounts Section */}
        <div className="mt-8 pt-8 border-t">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
                <Wallet className="w-5 h-5" />
                Your Accounts
              </h2>
              <p className="text-sm text-muted-foreground">
                Bank connections and manual accounts
              </p>
            </div>
          </div>
          <ConnectionsPanel 
            onAddAccount={() => navigate('/settings?tab=accounts')}
            onSync={async (connection) => {
              if (connection.provider === 'akahu') {
                await syncAkahu();
              }
            }}
            onReconnect={connectAkahu}
          />
        </div>
        
        {/* Open Banking Trust Box */}
        <div className="mt-6 p-4 rounded-xl bg-teal-50/50 dark:bg-teal-950/20 border border-teal-200/50 dark:border-teal-800/30" data-testid="open-banking-trust-box">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center flex-shrink-0">
              <Shield className="w-4 h-4 text-teal-600 dark:text-teal-400" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-teal-900 dark:text-teal-100 mb-2">
                Your bank connection is powered by{' '}
                <a 
                  href="https://www.akahu.nz" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-teal-700 dark:hover:text-teal-300 inline-flex items-center gap-1"
                >
                  Akahu
                  <ExternalLink className="w-3 h-3" />
                </a>
                , New Zealand's open banking platform.
              </p>
              <ul className="text-xs text-teal-700 dark:text-teal-300 space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
                  ARA Wealth can only view your account data
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
                  We cannot move money or perform transactions
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
                  Your bank login details are never shared with us
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Help text */}
        <div className="mt-8 p-4 rounded-lg bg-muted/30">
          <p className="text-sm text-muted-foreground">
            <strong>Tip:</strong> Most NZ banks let you download transactions as CSV from their website. 
            Look for "Export" or "Download" in your transaction history.
          </p>
        </div>
      </div>
      
      {/* Bank Connection Consent Panel */}
      <BankConnectionConsentPanel
        open={showBankConsentPanel}
        onOpenChange={setShowBankConsentPanel}
        onConfirm={handleBankConnectionConfirmed}
        loading={connectingBank}
      />
    </AppLayout>
  );
}
